import React, { useEffect, useState } from "react";
import { ShieldOff } from "lucide-react";
import AdminHeader from "@/components/admin/AdminHeader";
import { useAuth } from "@/contexts/AuthContext";

const STATUS = {
  active: { label: "Actif", cls: "bg-emerald-50 text-emerald-700" },
  expired: { label: "Expiré", cls: "bg-slate-100 text-slate-600" },
  withdrawn: { label: "Retiré", cls: "bg-red-50 text-red-700" },
  renewed: { label: "Renouvelé", cls: "bg-blue-50 text-blue-700" },
};
const SCOPE = { request: "Rappel tél.", marketing_phone: "Offres tél.", marketing_email: "Offres email", marketing: "Offres" };
const d = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

export default function AdminConsents() {
  const { authFetch } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const r = await authFetch("/admin/consents");
    const data = await r.json();
    setItems(Array.isArray(data) ? data : []);
  };
  useEffect(() => { load(); }, []);

  const withdraw = async (id) => {
    if (!window.confirm("Marquer ce consentement comme retiré ?")) return;
    await authFetch(`/admin/consents/${id}/withdraw`, { method: "POST" });
    load();
  };

  const count = (k) => (k === "all" ? items.length : items.filter((c) => c.status === k).length);
  const shown = filter === "all" ? items : items.filter((c) => c.status === filter);

  return (
    <div className="min-h-screen bg-md-bg" data-testid="admin-consents-page">
      <AdminHeader />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-serif text-4xl text-md-primary font-medium">Consentements</h1>
        <p className="text-md-muted mt-2 mb-8">Avant tout appel commercial, vérifiez que le consentement est <strong>actif</strong>. Preuves conservées 3 ans.</p>
        <div className="flex gap-2 mb-6 flex-wrap">
          {["all", "active", "renewed", "expired", "withdrawn"].map((k) => (
            <button key={k} data-testid={`consent-filter-${k}`} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === k ? "bg-md-primary text-white" : "bg-white text-md-muted border border-md-border hover:text-md-primary"}`}>
              {k === "all" ? "Tous" : STATUS[k].label} <span className="opacity-60">({count(k)})</span>
            </button>
          ))}
        </div>
        <div className="bg-white border border-md-border rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-consents-table">
            <thead className="text-[10px] uppercase tracking-widest text-md-muted text-left">
              <tr>{["Recueilli", "Contact", "Source", "Accords", "Expire", "Statut", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-md-border">
              {shown.map((c) => (
                <tr key={c.id} data-testid={`consent-row-${c.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{d(c.created_at)}</td>
                  <td className="px-4 py-3"><div className="font-medium text-md-primary">{c.full_name}</div><div className="text-xs text-md-muted">{c.email} · {c.phone || "—"}</div></td>
                  <td className="px-4 py-3 capitalize">{c.source}</td>
                  <td className="px-4 py-3">{c.scopes.map((s) => <span key={s.type} className="inline-block mr-1 mb-1 text-[10px] uppercase font-bold bg-md-bg text-md-primary px-2 py-0.5 rounded-full">{SCOPE[s.type]}</span>)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{d(c.expires_at)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${STATUS[c.status].cls}`}>{STATUS[c.status].label}</span></td>
                  <td className="px-4 py-3 text-right">
                    {c.status === "active" && (
                      <button data-testid={`consent-withdraw-${c.id}`} onClick={() => withdraw(c.id)} title="Retirer (demande orale du client)" className="w-8 h-8 rounded-full grid place-items-center bg-red-50 text-red-600 hover:bg-red-100"><ShieldOff className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-md-muted" data-testid="admin-consents-empty">Aucun consentement.</td></tr>}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
